import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Stack,
  Grid,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { useParams } from 'react-router';
import useModifiableParams from '../../hooks/use-modifiable-params';
import { Api } from '../../api';
import { format as dfFormat, parseISO } from 'date-fns';

function toISO(d: Date) {
  return dfFormat(d, 'yyyy-MM-dd');
}

function enumerateDates(startISO: string, endISO: string) {
  const outDates: string[] = [];
  const outLabels: string[] = [];
  const start = new Date(startISO);
  const end = new Date(endISO);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    outDates.push(toISO(d));
    outLabels.push(dfFormat(d, 'd MMM'));
  }
  return { isoDates: outDates, labels: outLabels };
}

function formatDuration(mins: number | null): string {
  if (mins === null || mins === 0) return '';
  const totalMins = Math.round(mins);
  const hours = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (hours === 0) return `${m} min`;
  return `${hours} hr ${m} min`;
}

export default function UserAppleHealthData() {
  const { id = '' } = useParams();
  const { getParams } = useModifiableParams({
    defaults: { timeframe: 'week' },
  });
  const params = getParams();

  let startDate: string | undefined = params.startDate;
  let endDate: string | undefined = params.endDate;
  if (!startDate || !endDate) {
    const today = new Date();
    if (params.timeframe === 'month') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      startDate = toISO(first);
      endDate = toISO(today);
    } else {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      startDate = toISO(start);
      endDate = toISO(today);
    }
  }

  const { isoDates, labels } = enumerateDates(startDate!, endDate!);

  const activitiesApi = Api.MetricActivity.useReadCollectionMetricActivities(
    id,
    {
      'date[after]': startDate!,
      'date[before]': endDate!,
    },
  );

  const dayMap: Record<
    string,
    {
      steps: number;
      exercise: number;
      sleepMins: number;
      timeInDaylight: number;
      heartRateVariability: number;
    }
  > = {};
  isoDates.forEach(
    (d) =>
      (dayMap[d] = {
        steps: 0,
        exercise: 0,
        sleepMins: 0,
        timeInDaylight: 0,
        heartRateVariability: 0,
      }),
  );
  const normKey = (s: string) => {
    try {
      return dfFormat(parseISO(s), 'yyyy-MM-dd');
    } catch {
      return (s || '').slice(0, 10);
    }
  };
  for (const a of activitiesApi.data?.member || []) {
    const key = normKey(a.date);
    if (!(key in dayMap)) continue;
    dayMap[key].steps += a.steps || 0;
    dayMap[key].exercise += a.exercise || 0;
    dayMap[key].sleepMins += a.sleep || 0;
    dayMap[key].timeInDaylight += a.timeInDaylight || 0;
    dayMap[key].heartRateVariability += a.heartRateVariability || 0;
  }

  const stepsData = isoDates.map((d) => dayMap[d]?.steps ?? 0);
  const exerciseData = isoDates.map((d) => dayMap[d]?.exercise ?? 0);
  const sleepData = isoDates.map((d) => {
    const mins = dayMap[d]?.sleepMins ?? 0;
    return Number((mins / 60).toFixed(2));
  });
  const timeInDaylightData = isoDates.map(
    (d) => dayMap[d]?.timeInDaylight ?? 0,
  );
  const heartRateVariabilityData = isoDates.map(
    (d) => dayMap[d]?.heartRateVariability ?? 0,
  );

  const avg = (arr: number[]) => {
    const nums = arr.filter((n) => n > 0);
    if (!nums.length) return 0;
    return nums.reduce((a, b) => a + b, 0) / nums.length;
  };

  const avgSteps = Math.round(avg(stepsData));
  const avgExercise = Math.round(avg(exerciseData));
  const avgSleepMins = Math.round(
    avg(isoDates.map((d) => dayMap[d]?.sleepMins ?? 0)),
  );
  const avgSleepH = Math.floor(avgSleepMins / 60);
  const avgSleepM = avgSleepMins % 60;
  const avgTimeInDaylightMins = Math.round(
    avg(isoDates.map((d) => dayMap[d]?.timeInDaylight ?? 0)),
  );
  const avgHeartRateVariability = Math.round(avg(heartRateVariabilityData));

  return (
    <Stack spacing={3}>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Card>
            <CardHeader
              sx={{ alignItems: 'flex-start' }}
              title={<Typography variant="h6">Exercise Minutes</Typography>}
              action={
                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Stack alignItems="flex-start" spacing={1}>
                    <Typography variant="subtitle2">Average/day</Typography>
                    <Typography variant="subtitle1">
                      {avgExercise}{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        min
                      </Typography>
                    </Typography>
                  </Stack>
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ width: '100%', height: 300 }}>
                <BarChart
                  series={[
                    {
                      data: exerciseData,
                      color: '#5A7280',
                    },
                  ]}
                  xAxis={[
                    {
                      scaleType: 'band',
                      data: labels,
                      disableLine: true,
                      disableTicks: true,
                      categoryGapRatio: 0.6,
                    },
                  ]}
                  yAxis={[
                    {
                      disableLine: true,
                      disableTicks: true,
                      min: 0,
                    },
                  ]}
                  grid={{ horizontal: true, vertical: false }}
                  margin={{ left: 40, right: 30, top: 20, bottom: 50 }}
                  slotProps={{
                    bar: {
                      style: {
                        borderRadius: '8px 8px 0 0',
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6}>
          <Card>
            <CardHeader
              sx={{ alignItems: 'flex-start' }}
              title={<Typography variant="h6">Steps</Typography>}
              action={
                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Stack alignItems="flex-start" spacing={1}>
                    <Typography variant="subtitle2">Average/day</Typography>
                    <Typography variant="subtitle1">
                      {avgSteps.toLocaleString()}{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        steps
                      </Typography>
                    </Typography>
                  </Stack>
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ width: '100%', height: 300 }}>
                <BarChart
                  series={[
                    {
                      data: stepsData,
                      color: '#5A7280',
                    },
                  ]}
                  xAxis={[
                    {
                      scaleType: 'band',
                      data: labels,
                      disableLine: true,
                      disableTicks: true,
                      categoryGapRatio: 0.6,
                    },
                  ]}
                  yAxis={[
                    {
                      disableLine: true,
                      disableTicks: true,
                      min: 0,
                    },
                  ]}
                  grid={{ horizontal: true, vertical: false }}
                  margin={{ left: 40, right: 30, top: 20, bottom: 50 }}
                  slotProps={{
                    bar: {
                      style: {
                        borderRadius: '8px 8px 0 0',
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6}>
          <Card>
            <CardHeader
              sx={{ alignItems: 'flex-start' }}
              title={<Typography variant="h6">Sleep</Typography>}
              action={
                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Stack alignItems="flex-start" spacing={1}>
                    <Typography variant="subtitle2">Average/day</Typography>
                    <Typography variant="subtitle1">
                      {avgSleepH}{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        hr
                      </Typography>{' '}
                      {avgSleepM}{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        min
                      </Typography>
                    </Typography>
                  </Stack>
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ width: '100%', height: 300 }}>
                <BarChart
                  series={[
                    {
                      data: sleepData,
                      color: '#5A7280',
                      valueFormatter: (v) => formatDuration(v ? v * 60 : null),
                    },
                  ]}
                  xAxis={[
                    {
                      scaleType: 'band',
                      data: labels,
                      disableLine: true,
                      disableTicks: true,
                      categoryGapRatio: 0.6,
                    },
                  ]}
                  yAxis={[
                    {
                      disableLine: true,
                      disableTicks: true,
                      min: 0,
                    },
                  ]}
                  grid={{ horizontal: true, vertical: false }}
                  margin={{ left: 40, right: 30, top: 20, bottom: 50 }}
                  slotProps={{
                    bar: {
                      style: {
                        borderRadius: '8px 8px 0 0',
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6}>
          <Card>
            <CardHeader
              sx={{ alignItems: 'flex-start' }}
              title={<Typography variant="h6">Time in Daylight</Typography>}
              action={
                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Stack alignItems="flex-start" spacing={1}>
                    <Typography variant="subtitle2">Average/day</Typography>
                    <Typography variant="subtitle1">
                      {avgTimeInDaylightMins}{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        min
                      </Typography>
                    </Typography>
                  </Stack>
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ width: '100%', height: 300 }}>
                <BarChart
                  series={[
                    {
                      data: timeInDaylightData,
                      color: '#5A7280',
                      valueFormatter: (v) => formatDuration(v),
                    },
                  ]}
                  xAxis={[
                    {
                      scaleType: 'band',
                      data: labels,
                      disableLine: true,
                      disableTicks: true,
                      categoryGapRatio: 0.6,
                    },
                  ]}
                  yAxis={[{ disableLine: true, disableTicks: true, min: 0 }]}
                  grid={{ horizontal: true, vertical: false }}
                  margin={{ left: 40, right: 30, top: 20, bottom: 50 }}
                  slotProps={{
                    bar: { style: { borderRadius: '8px 8px 0 0' } },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6}>
          <Card>
            <CardHeader
              sx={{ alignItems: 'flex-start' }}
              title={
                <Typography variant="h6">Heart Rate Variability</Typography>
              }
              action={
                <Box
                  sx={{
                    bgcolor: 'var(--mui-palette-background-level1)',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Stack alignItems="flex-start" spacing={1}>
                    <Typography variant="subtitle2">Average/day</Typography>
                    <Typography variant="subtitle1">
                      {avgHeartRateVariability}{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.secondary"
                      >
                        ms
                      </Typography>
                    </Typography>
                  </Stack>
                </Box>
              }
            />
            <CardContent>
              <Box sx={{ width: '100%', height: 300 }}>
                <LineChart
                  series={[
                    {
                      data: heartRateVariabilityData,
                      color: '#5A7280',
                      showMark: false,
                      curve: 'linear',
                    },
                  ]}
                  xAxis={[
                    {
                      scaleType: 'point',
                      data: labels,
                      disableLine: true,
                      disableTicks: true,
                    },
                  ]}
                  yAxis={[
                    {
                      disableLine: true,
                      disableTicks: true,
                      min: 0,
                    },
                  ]}
                  grid={{ horizontal: true, vertical: false }}
                  margin={{ left: 40, right: 30, top: 20, bottom: 50 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
